import { Filter, X } from 'lucide-react'
import { type ReactNode, useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet'
import { useBodyScrollLock } from '@/hooks/use-body-scroll-lock'

import { FacetCombobox } from './facet-combobox'
import type { SupplierFilterOptions, SupplierFilterState } from './filter-state'

const STATUS_FILTERS = [
  { key: 'founding', label: 'Founding Suppliers' },
  { key: 'verified', label: 'Verified Suppliers' },
  { key: 'favorites', label: 'Favorite Suppliers' }
] as const

type StatusFilterKey = (typeof STATUS_FILTERS)[number]['key']

type Props = {
  value: SupplierFilterState
  onChange: (next: SupplierFilterState) => void
  options: SupplierFilterOptions
  /** renders in STATUS_FILTERS order regardless of the order passed. */
  statusFilters?: readonly StatusFilterKey[]
  /** page-owned filters shown first in the sheet, counted and cleared with the rest. */
  extra?: { content: ReactNode; activeCount: number; onClear: () => void }
}

export function SupplierFilters({
  value,
  onChange,
  options,
  statusFilters = [],
  extra
}: Props) {
  const [open, setOpen] = useState(false)
  const shownStatusFilters = STATUS_FILTERS.filter(filter =>
    statusFilters.includes(filter.key)
  )
  const statusCount = shownStatusFilters.filter(
    filter => value[filter.key]
  ).length
  /** a facet with no options isn't rendered, and a caller may surface it as its
   *  own toolbar control instead (the companies pages do this with Country).
   *  counting or clearing it here would report a selection this sheet doesn't
   *  contain. */
  const rendered = {
    countries: options.countries.length > 0,
    capabilityGroups: options.capabilityGroups.length > 0,
    industries: options.industries.length > 0,
    types: options.types.length > 0,
    certifications: options.certifications.length > 0
  }

  const activeCount =
    (rendered.countries ? value.countries.length : 0) +
    (rendered.capabilityGroups ? value.capabilityGroups.length : 0) +
    (rendered.industries ? value.industries.length : 0) +
    (rendered.types ? value.types.length : 0) +
    (rendered.certifications ? value.certifications.length : 0) +
    statusCount +
    (extra?.activeCount ?? 0)

  const hasOptions = Object.values(rendered).some(Boolean) || Boolean(extra)

  useBodyScrollLock(open)

  /** nothing to filter on (e.g. a tiny mode subset where every supplier
   * shares the same facets), drop the button entirely.
   */
  if (!hasOptions) return null

  return (
    <>
      <Button
        variant='outline'
        onClick={() => setOpen(true)}
        className='w-full gap-1.5 sm:w-auto'
      >
        <Filter className='size-3.5' />
        Filter
        {activeCount > 0 && (
          <Badge variant='secondary' className='ml-1 h-4 px-1.5 text-[10px]'>
            {activeCount}
          </Badge>
        )}
      </Button>

      {/** manual backdrop. Sheet is modal={false} so the Combobox popup
       * (which portals to body) can receive clicks. radix Dialog's modal
       * mode would trap pointer events to its content tree and the popup
       * would render but be unclickable.
       */}
      {open && (
        <div
          className='fixed inset-0 z-40 bg-black/40'
          onClick={() => setOpen(false)}
        />
      )}

      <Sheet open={open} onOpenChange={setOpen} modal={false}>
        <SheetContent className='w-full sm:max-w-xl'>
          <SheetHeader>
            <SheetTitle>Filters</SheetTitle>
            <SheetDescription>
              Narrow the list using the filters below.
            </SheetDescription>
          </SheetHeader>

          <div className='flex flex-1 flex-col gap-6 overflow-y-auto px-4 py-4 sm:px-6'>
            {extra?.content}
            {shownStatusFilters.length > 0 ? (
              <div className='flex flex-col gap-2'>
                <Label className='text-xs font-medium text-foreground'>
                  Show
                </Label>
                <div className='flex flex-col gap-2.5'>
                  {shownStatusFilters.map(filter => (
                    <label
                      key={filter.key}
                      className='flex cursor-pointer items-center gap-2 text-sm'
                    >
                      <Checkbox
                        checked={value[filter.key]}
                        onCheckedChange={checked =>
                          onChange({ ...value, [filter.key]: checked === true })
                        }
                      />
                      {filter.label}
                    </label>
                  ))}
                </div>
              </div>
            ) : null}
            {options.types.length > 0 ? (
              <FacetCombobox
                variant='block'
                label='Type'
                placeholder='Select types…'
                options={options.types}
                selected={value.types}
                onSelectedChange={types => onChange({ ...value, types })}
              />
            ) : null}
            {options.countries.length > 0 ? (
              <FacetCombobox
                variant='block'
                label='Country'
                placeholder='Select countries…'
                options={options.countries}
                selected={value.countries}
                onSelectedChange={countries =>
                  onChange({ ...value, countries })
                }
              />
            ) : null}
            {options.capabilityGroups.length > 0 ? (
              <FacetCombobox
                variant='block'
                label='Operations & Capabilities'
                placeholder='Select capabilities…'
                options={options.capabilityGroups}
                selected={value.capabilityGroups}
                onSelectedChange={capabilityGroups =>
                  onChange({ ...value, capabilityGroups })
                }
              />
            ) : null}
            {options.industries.length > 0 ? (
              <FacetCombobox
                variant='block'
                label='Industries'
                placeholder='Select industries…'
                options={options.industries}
                selected={value.industries}
                onSelectedChange={industries =>
                  onChange({ ...value, industries })
                }
              />
            ) : null}
            {options.certifications.length > 0 ? (
              <FacetCombobox
                variant='block'
                label='Certifications'
                placeholder='Select certifications…'
                options={options.certifications}
                selected={value.certifications}
                onSelectedChange={certifications =>
                  onChange({ ...value, certifications })
                }
              />
            ) : null}
          </div>

          <SheetFooter className='flex-col-reverse sm:flex-row sm:items-center sm:justify-between'>
            <Button
              variant='ghost'
              size='sm'
              disabled={activeCount === 0}
              onClick={() => {
                const next: SupplierFilterState = {
                  ...value,
                  ...(rendered.countries ? { countries: [] } : {}),
                  ...(rendered.capabilityGroups
                    ? { capabilityGroups: [] }
                    : {}),
                  ...(rendered.industries ? { industries: [] } : {}),
                  ...(rendered.types ? { types: [] } : {}),
                  ...(rendered.certifications ? { certifications: [] } : {})
                }
                for (const filter of shownStatusFilters)
                  next[filter.key] = false
                onChange(next)
                extra?.onClear()
              }}
              className='w-full gap-1 sm:w-auto'
            >
              <X className='size-3.5' />
              Clear filters
            </Button>
            {/* the sheet covers the whole list on a phone, so there has to be
                a way back to the results other than the X. */}
            <Button
              size='lg'
              className='w-full sm:hidden'
              onClick={() => setOpen(false)}
            >
              Show results
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  )
}
